import { useCallback, useEffect, useState } from "react";
import { API_URL, useAuth } from "./AuthContext";

// While a review is open, re-check this often (webhooks can't reach localhost, and
// the status endpoint syncs from Sumsub itself)
const PENDING_POLL_MS = 15000;

// Sumsub verification status from the API:
// none | init | pending | verified | retry | rejected
export function useKyc() {
  const { user, token } = useAuth();
  const [status, setStatus] = useState(user?.kyc?.status || "none");

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/kyc/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.kyc?.status) setStatus(data.kyc.status);
    } catch (err) {
      console.error("Failed to load KYC status:", err);
    }
  }, [token]);

  // Rejects with a message the KYC modal can show
  const getAccessToken = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/kyc/access-token`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "We couldn't start verification. Please try again.");
    return data.token;
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (status !== "pending") return undefined;
    const id = setInterval(refresh, PENDING_POLL_MS);
    return () => clearInterval(id);
  }, [status, refresh]);

  return { status, refresh, getAccessToken };
}
